/** @format */

import {
	Alert,
	Avatar,
	Box,
	Button,
	Dialog,
	DialogContent,
	FormControl,
	Grid,
	InputLabel,
	OutlinedInput,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import AnimateButton from "./AnimateBtn";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
	IUserUpdatePayload,
	EmptyPhoneContact,
} from "../../../utility/types/userType";
import { buttonColors, colors } from "../../../utility/types/constant";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, store } from "../../../redux/store/store";
import { update } from "../../../redux/actions/userActions";
import {
	selectCurrentUser,
	selectIsLoading,
} from "../../../redux/selectors/userSelector";
import { LoadingSpinner } from "../../allComponents/AllComponents";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../../signUp/SignUp.css";
import React from "react";

interface UpdateAccountModalProps {
	open: boolean;
	handleClose: () => void;
	userData: any;
}

const UpdateAccountModal = (props: UpdateAccountModalProps) => {
	const theme = useTheme();

	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(selectIsLoading);

	const [updateSuccess, setUpdateSuccess] = useState(false);

	const [errorMessageAlert, setErrorMessageAlert] = useState("");

	const { open, handleClose, userData } = props;

	const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));

	const [imagePreview, setImagePreview] = useState(
		userData?.profileImage ?? ""
	);

	const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);

	const [initialValues, setInitialValues] = useState<IUserUpdatePayload>({
		firstName: "",
		lastName: "",
		middleName: "",
		email: "",
		phone: userData?.phone ?? EmptyPhoneContact,
	});

	useEffect(() => {
		if (userData) {
			setInitialValues({
				firstName: userData.firstName || "",
				lastName: userData.lastName || "",
				middleName: userData.middleName || "",
				email: userData.email || "",
				phone: userData.phone || EmptyPhoneContact,
			});
		}
	}, [userData]);

	const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Check if any file is selected
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0];

			// const filePreview = URL.createObjectURL(selectedFile)

			setImagePreview(URL.createObjectURL(file));

			setSelectedFile(file);
		}
	};

	const handleSubmit = async (values: IUserUpdatePayload, actions: any) => {
		let formData = new FormData();

		// adding updated fields to formData
		Object.keys(values).forEach((key) => {
			const value = values[key as keyof IUserUpdatePayload];

			if (key === "phone" && value && typeof value === "object") {
				// Append phone as a JSON string
				formData.append("phone", JSON.stringify(value));
				return;
			}

			if (
				key !== "_id" &&
				key !== "userId" &&
				key !== "phone" &&
				value !== undefined &&
				value !== null
			) {
				formData.append(key, value?.toString());
			}
		});

		// handle file upload;
		if (selectedFile) {
			formData.append("profileImage", selectedFile);
		}

		try {
			const currentUser = selectCurrentUser(store.getState());

			if (currentUser && currentUser._id) {
				await dispatch(
					update({
						formData,
						userId: currentUser._id,
					})
				).unwrap();

				setUpdateSuccess(true);
				actions.setSubmitting(true);
			}
		} catch (err: any) {
			setUpdateSuccess(false);
			setErrorMessageAlert(err.message || "Unknown error occurred.");
			actions.setSubmitting(false);
		}
	};

	const handleCloseSuccessAlert = () => {
		setUpdateSuccess(false);
	};

	const handleCloseErrorAlert = () => {
		setErrorMessageAlert(""); // Set the error message to an empty string to hide the alert
	};

	const PhoneTextField = React.forwardRef<HTMLInputElement, any>(
		function PhoneTextField(props, ref) {
			const { value, onChange, ...rest } = props;

			return (
				<TextField
					{...rest}
					inputRef={ref}
					value={value ?? ""}
					onChange={(e) => onChange(e.target.value)}
				/>
			);
		}
	);

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
			{updateSuccess && (
				<Stack sx={{ width: "100%" }} spacing={2}>
					<Alert severity="success" onClose={handleCloseSuccessAlert}>
						Success - <strong>Account Updated Successfully.</strong>
					</Alert>
				</Stack>
			)}

			{errorMessageAlert && (
				<Stack sx={{ width: "100%" }} spacing={2}>
					<Alert severity="error" onClose={handleCloseErrorAlert}>
						Error - <strong>{errorMessageAlert}</strong>
					</Alert>
				</Stack>
			)}

			<DialogContent>
				<Grid container direction="column" justifyContent="center" spacing={2}>
					<Grid
						item
						xs={12}
						container
						alignItems="center"
						justifyContent="center">
						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle1" sx={{ fontSize: "2rem" }}>
								Update Account
							</Typography>
						</Box>
					</Grid>
				</Grid>

				<Formik
					initialValues={initialValues}
					onSubmit={handleSubmit}
					enableReinitialize>
					{({ values, handleChange, handleBlur, setFieldValue }) => (
						<Form>
							<Grid container spacing={matchDownSM ? 0 : 2}>
								<Grid item xs={12} sm={6} sx={{ mt: 2 }}>
									<TextField
										fullWidth
										label="First Name"
										name="firstName"
										margin="normal"
										type="text"
										sx={{
											...theme.typography,
											fontSize: "1.2rem",
											"& .MuiOutlinedInput-root": {
												"& fieldset": {
													borderColor: "black", // Solid black border
												},
												"&:hover fieldset": {
													borderColor: "black", // Solid black border on hover
												},
												"&.Mui-focused fieldset": {
													borderColor: "black", // Solid black border when focused
												},
											},
										}}
										onChange={handleChange}
										value={values.firstName}
									/>
								</Grid>

								<Grid item xs={12} sm={6} sx={{ mt: 2 }}>
									<TextField
										fullWidth
										label="Last Name"
										name="lastName"
										margin="normal"
										type="text"
										sx={{
											...theme.typography,
											fontSize: "1.2rem",
											"& .MuiOutlinedInput-root": {
												"& fieldset": {
													borderColor: "black",
												},
												"&:hover fieldset": {
													borderColor: "black",
												},
												"&.Mui-focused fieldset": {
													borderColor: "black",
												},
											},
										}}
										onChange={handleChange}
										value={values.lastName}
									/>
								</Grid>
							</Grid>

							<TextField
								fullWidth
								label="Email"
								name="email"
								margin="normal"
								type="email"
								sx={{
									...theme.typography,
									fontSize: "1.2rem",
									mt: 3,
									"& .MuiOutlinedInput-root": {
										"& fieldset": {
											borderColor: "black",
										},
										"&:hover fieldset": {
											borderColor: "black",
										},
										"&.Mui-focused fieldset": {
											borderColor: "black",
										},
									},
								}}
								onChange={handleChange}
								value={values.email}
							/>

							<Box sx={{ mt: 3 }}>
								<PhoneInput
									defaultCountry="US"
									international
									value={values.phone?.e164 ?? undefined}
									onChange={(val) =>
										setFieldValue("phone", {
											...(values.phone ?? EmptyPhoneContact),
											e164: val ?? "",
										})
									}
									className="phone-input-container"
									style={{
										width: "100%",
									}}
									inputComponent={PhoneTextField}
									inputProps={{
										fullWidth: true,
										label: "Phone Number",
										variant: "outlined",
										margin: "normal",
										sx: {
											...theme.typography,
											fontSize: "1.2rem",
											"& .MuiOutlinedInput-root": {
												"& fieldset": {
													borderColor: "black",
												},
											},
										},
									}}
								/>{" "}
							</Box>

							<Grid container spacing={2} sx={{ mt: 3, alignItems: "center" }}>
								<Grid item>
									<input
										style={{ display: "none" }}
										id="upload-photo"
										type="file"
										name="upload-photo"
										accept="image/*"
										onChange={handleSelectFile}
									/>
									<label htmlFor="upload-photo">
										<Button
											sx={{
												color: colors.white,
												backgroundColor: buttonColors.primary,
												"&:hover": {
													backgroundColor: buttonColors.primary,
												},
											}}
											size="medium"
											variant="contained"
											component="span">
											Add photo
										</Button>
									</label>
								</Grid>

								<Grid item xs />

								<Grid item>
									<AnimateButton>
										<Button
											disabled={isLoading}
											size="large"
											type="submit"
											variant="contained"
											sx={{
												color: colors.white,
												backgroundColor: colors.secondPrimary,
												"&:hover": {
													backgroundColor: colors.secondPrimary,
												},
												width: 200,
											}}>
											{isLoading ? (
												<LoadingSpinner
													loading={isLoading}
													color={colors.white}
												/>
											) : (
												"Update"
											)}
										</Button>
									</AnimateButton>
								</Grid>
							</Grid>

							<Grid item container sx={{ mt: 4, justifyContent: "center" }}>
								<Box display="flex" justifyContent="center" alignItems="center">
									{imagePreview && (
										<Avatar
											src={imagePreview}
											alt="Profile Preview"
											sx={{
												height: 250,
												mb: 2,
												width: 250,
											}}
										/>
									)}
								</Box>
							</Grid>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

UpdateAccountModal.propType = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	userData: PropTypes.object,
};

export default UpdateAccountModal;
